/**
 * Template parser — converts a raw template string into an Abstract Syntax Tree
 * (AST) that the renderer can walk.
 *
 * Supported syntax:
 *   {{key}}                      — variable interpolation (dot-notation: {{a.b.c}})
 *   {{t "text"}}                  — translation / localization
 *   {{> partial_name}}           — include / partial
 *   <!-- BEGIN: name -->…<!-- END: name -->     — loop block
 *   <!-- IF: name -->…<!-- ELSE: name -->…<!-- ENDIF: name -->  — conditional
 *
 * AST node shapes:
 *   { type: "text",        value: string }
 *   { type: "variable",    path: string[] }
 *   { type: "translate",   text: string }
 *   { type: "partial",     name: string }
 *   { type: "block",       name: string,  children: Node[] }
 *   { type: "conditional", path: string[], children: Node[], elseChildren: Node[]|null }
 */

// ──────────────────────────────────────────────────────────────────────────────
// Regex that captures every token type in one pass.
// Groups:  1=partial  2=variable  3=begin  4=end  5=if  6=else  7=endif
// ──────────────────────────────────────────────────────────────────────────────
// Regex that captures every token type in one pass.
// Supports both {{key}} and ${key} syntax for backward compatibility.
// Groups:  1=partial  2=translate  3={{variable}}  4=${variable}  5=begin  6=end  7=if  8=else  9=endif
// ──────────────────────────────────────────────────────────────────────────────
const TOKEN_PATTERN = new RegExp(
    "\\{\\{\\s*>\\s*([\\w.-]+)\\s*\\}\\}" + // {{> partial}}
    "|\\{\\{\\s*t\\s+\"([^\"]+)\"\\s*\\}\\}" + // {{t "text"}} — translation
    "|\\{\\{\\s*(\\w+(?:\\.\\w+)*)\\s*\\}\\}" + // {{variable}}
    "|\\$\\{\\s*(\\w+(?:\\.\\w+)*)\\s*\\}" + // ${variable} (legacy)
    "|<!--\\s*BEGIN:\\s*(\\w+)\\s*-->" + // <!-- BEGIN: x -->
    "|<!--\\s*END:\\s*(\\w+)\\s*-->" + // <!-- END: x -->
    "|<!--\\s*IF:\\s*(\\w+(?:\\.\\w+)*)\\s*-->" + // <!-- IF: x -->
    "|<!--\\s*ELSE:\\s*(\\w+)\\s*-->" + // <!-- ELSE: x -->
    "|<!--\\s*ENDIF:\\s*(\\w+)\\s*-->", // <!-- ENDIF: x -->
    "g",
)

// ──────────────────────────────────────────────────────────────────────────────
// Tokenizer
// ──────────────────────────────────────────────────────────────────────────────

/**
 * Splits a raw template string into a flat list of typed tokens.
 *
 * @param {string} template - Raw template string.
 * @returns {Array<Object>} Ordered token list.
 */
function tokenize(template) {
    const tokens = []
    let lastIndex = 0
    let match

    // Reset regex state (global flag)
    TOKEN_PATTERN.lastIndex = 0

    while ((match = TOKEN_PATTERN.exec(template)) !== null) {
        // Capture plain text before this token
        if (match.index > lastIndex) {
            tokens.push({ type: "text", value: template.slice(lastIndex, match.index) })
        }

        if (match[1] != null) tokens.push({ type: "partial", name: match[1] })
        else if (match[2] != null) tokens.push({ type: "translate", text: match[2] })
        else if (match[3] != null) tokens.push({ type: "variable", key: match[3] })
        else if (match[4] != null) tokens.push({ type: "variable", key: match[4] })
        else if (match[5] != null) tokens.push({ type: "begin", name: match[5] })
        else if (match[6] != null) tokens.push({ type: "end", name: match[6] })
        else if (match[7] != null) tokens.push({ type: "if", name: match[7] })
        else if (match[8] != null) tokens.push({ type: "else", name: match[8] })
        else if (match[9] != null) tokens.push({ type: "endif", name: match[9] })

        lastIndex = TOKEN_PATTERN.lastIndex
    }

    // Trailing text
    if (lastIndex < template.length) {
        tokens.push({ type: "text", value: template.slice(lastIndex) })
    }

    return tokens
}

// ──────────────────────────────────────────────────────────────────────────────
// AST Builder — recursive descent over the flat token list
// ──────────────────────────────────────────────────────────────────────────────

/**
 * Converts a flat token list into a nested AST.
 *
 * @param {Array<Object>} tokens - Flat token array from tokenize().
 * @returns {Array<Object>} AST node tree.
 */
function buildAST(tokens) {
    const nodes = []
    let i = 0

    while (i < tokens.length) {
        const token = tokens[i]

        switch (token.type) {
            case "text":
                nodes.push({ type: "text", value: token.value })
                i++
                break

            case "variable":
                nodes.push({ type: "variable", path: token.key.split(".") })
                i++
                break

            case "translate":
                nodes.push({ type: "translate", text: token.text })
                i++
                break

            case "partial":
                nodes.push({ type: "partial", name: token.name })
                i++
                break

            case "begin": {
                const result = collectBlock(tokens, i + 1, token.name)
                nodes.push({ type: "block", name: token.name, children: result.nodes })
                i = result.nextIndex
                break
            }

            case "if": {
                const result = collectConditional(tokens, i + 1, token.name)
                nodes.push({
                    type: "conditional",
                    path: token.name.split("."),
                    children: result.children,
                    elseChildren: result.elseChildren,
                })
                i = result.nextIndex
                break
            }

            default:
                // Orphan END / ELSE / ENDIF — skip with warning
                console.warn(`Template engine: unexpected ${token.type} token "${token.name}"`)
                i++
        }
    }

    return nodes
}

/**
 * Collects tokens between a BEGIN and its matching END, respecting nesting
 * of same-name blocks, then recursively builds an AST from the inner tokens.
 *
 * @param {Array<Object>} tokens     - Full token list.
 * @param {number}        startIndex - Index right after the opening BEGIN token.
 * @param {string}        blockName  - The block name to match.
 * @returns {{ nodes: Array<Object>, nextIndex: number }}
 */
function collectBlock(tokens, startIndex, blockName) {
    const inner = []
    let i = startIndex
    let depth = 1

    while (i < tokens.length) {
        const token = tokens[i]

        if (token.type === "begin" && token.name === blockName) {
            depth++
            inner.push(token)
        } else if (token.type === "end" && token.name === blockName) {
            depth--
            if (depth === 0) {
                return { nodes: buildAST(inner), nextIndex: i + 1 }
            }
            inner.push(token)
        } else {
            inner.push(token)
        }
        i++
    }

    console.warn(`Template engine: missing <!-- END: ${blockName} -->`)
    return { nodes: buildAST(inner), nextIndex: i }
}

/**
 * Collects tokens for an IF / ELSE / ENDIF structure, respecting nesting of
 * same-name conditionals, then recursively builds ASTs for both branches.
 *
 * @param {Array<Object>} tokens     - Full token list.
 * @param {number}        startIndex - Index right after the opening IF token.
 * @param {string}        condName   - The conditional name to match.
 * @returns {{ children: Array<Object>, elseChildren: Array<Object>|null, nextIndex: number }}
 */
function collectConditional(tokens, startIndex, condName) {
    const ifTokens = []
    const elseTokens = []
    let inElse = false
    let i = startIndex
    let depth = 1

    while (i < tokens.length) {
        const token = tokens[i]

        if (token.type === "if" && token.name === condName) {
            depth++
        } else if (token.type === "endif" && token.name === condName) {
            depth--
            if (depth === 0) {
                return {
                    children: buildAST(ifTokens),
                    elseChildren: elseTokens.length > 0 ? buildAST(elseTokens) : null,
                    nextIndex: i + 1,
                }
            }
        } else if (token.type === "else" && token.name === condName && depth === 1) {
            inElse = true
            i++
            continue
        }

        if (inElse) {
            elseTokens.push(token)
        } else {
            ifTokens.push(token)
        }
        i++
    }

    console.warn(`Template engine: missing <!-- ENDIF: ${condName} -->`)
    return {
        children: buildAST(ifTokens),
        elseChildren: elseTokens.length > 0 ? buildAST(elseTokens) : null,
        nextIndex: i,
    }
}

// ──────────────────────────────────────────────────────────────────────────────
// Public API
// ──────────────────────────────────────────────────────────────────────────────

/**
 * Parses a raw template string into an AST node tree.
 *
 * @param {string} template - Raw template string.
 * @returns {Array<Object>} AST nodes ready for the renderer.
 */
export function parse(template) {
    return buildAST(tokenize(template))
}
